import { useEffect, useState, useCallback } from 'react';
import { Loader2, Plus, Trash2, Save, X, Link2, Target, Skull } from 'lucide-react';
import {
  getMonsterDrop, addDropTarget, updateDropTarget, deleteDropTarget,
  editDropPool, saveDropLimit, deleteDropLimit,
  type MonsterDropResponse, type DropTarget, type DropPoolItem, type DropLimit,
  type EventSummary, type GameItem,
} from '@/shared/services/eventManagerService';
import { useToast } from '@/shared/contexts/ToastContext';
import { GameItemPicker, ItemKindInline } from './GameItem';

const tkey = (t: { dungeonIndex: number; mobSpeciesIdx: number }) => `${t.dungeonIndex}:${t.mobSpeciesIdx}`;
const emptyTarget = (): DropTarget => ({
  dungeonIndex: 0, mobSpeciesIdx: 0, authorityType: 0, perfectDrop: 0,
  multiDropType: 0, multiDropMin: 0, multiDropMax: 0, dropPoolId: 0, spawnChannels: '',
});

export default function MonsterDropEditor({ event }: { event: EventSummary }) {
  const { notify } = useToast();
  const id = event.eventId;
  const [data, setData] = useState<MonsterDropResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [sel, setSel] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { setData(await getMonsterDrop(id)); }
    catch (err: any) { notify('error', err?.detail || err?.message || 'Erro'); }
    finally { setLoading(false); }
  }, [id, notify]);
  useEffect(() => { load(); }, [load]);

  if (loading || !data) return <div className="py-16 flex justify-center text-white/40"><Loader2 size={20} className="animate-spin" /></div>;

  const selected = data.targets.find(t => tkey(t) === sel) ?? null;

  return (
    <div className="space-y-6">
      <div className="surface-card rounded-xl p-5">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-sm font-bold text-white uppercase tracking-wide flex items-center gap-2"><Target size={15} className="text-violet-400" /> Dungeons / Targets</h2>
          <button onClick={() => setAdding(true)} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold btn-primary cursor-pointer"><Plus size={13} /> Adicionar</button>
        </div>
        {data.targets.length === 0 ? (
          <p className="text-xs text-white/40 py-6 text-center">Nenhum target. Adicione uma dungeon + mob.</p>
        ) : (
          <div className="space-y-1.5">
            {data.targets.map(t => {
              const ref = data.poolRefCounts[String(t.dropPoolId)] ?? 0;
              return (
                <button key={tkey(t)} onClick={() => setSel(tkey(t))}
                  className={`w-full flex items-center gap-3 p-2.5 rounded-lg border transition cursor-pointer text-left
                    ${sel === tkey(t) ? 'border-[#00e1d7]/50 bg-[#00e1d7]/[0.06]' : 'border-white/[0.06] hover:bg-white/[0.03]'}`}>
                  <Skull size={16} className="text-white/40 shrink-0" />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm text-white">Dungeon <span className="font-mono">{t.dungeonIndex}</span> · Mob <span className="font-mono">{t.mobSpeciesIdx}</span></p>
                    <p className="text-[10px] text-white/40">Pool #{t.dropPoolId} · {(data.pools[String(t.dropPoolId)]?.length ?? 0)} itens{ref > 1 ? ` · compartilhado ×${ref}` : ''}</p>
                  </div>
                  {ref > 1 && <Link2 size={13} className="text-amber-400 shrink-0" />}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {selected && <TargetDetail key={sel!} eventId={id} data={data} target={selected} onChanged={load} onDeselect={() => setSel(null)} />}

      {adding && <AddTargetModal eventId={id} onClose={() => setAdding(false)} onAdded={async () => { setAdding(false); await load(); }} />}
    </div>
  );
}

/* ------------------------------------------------ detalhe do target (campos + pool + limite) */
function TargetDetail({ eventId, data, target, onChanged, onDeselect }: {
  eventId: number; data: MonsterDropResponse; target: DropTarget; onChanged: () => void; onDeselect: () => void;
}) {
  const { notify } = useToast();
  const [t, setT] = useState<DropTarget>(target);
  const [busy, setBusy] = useState(false);
  const set = (k: keyof DropTarget, v: any) => setT(p => ({ ...p, [k]: v }));

  const saveTarget = async () => {
    setBusy(true);
    try { await updateDropTarget(eventId, target.dungeonIndex, target.mobSpeciesIdx, t); notify('success', 'Target salvo'); onChanged(); }
    catch (err: any) { notify('error', err?.detail || err?.message || 'Erro'); }
    finally { setBusy(false); }
  };
  const delTarget = async () => {
    if (!confirm('Remover este target?')) return;
    try { await deleteDropTarget(eventId, target.dungeonIndex, target.mobSpeciesIdx); notify('success', 'Removido'); onDeselect(); onChanged(); }
    catch (err: any) { notify('error', err?.detail || err?.message || 'Erro'); }
  };

  const poolItems = data.pools[String(target.dropPoolId)] ?? [];
  const poolRef = data.poolRefCounts[String(target.dropPoolId)] ?? 0;
  const poolName = data.poolNames[String(target.dropPoolId)] ?? '';

  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <div className="surface-card rounded-xl p-5 space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-bold text-white">Target · D{target.dungeonIndex} / M{target.mobSpeciesIdx}</h3>
          <button onClick={delTarget} className="text-red-400/80 hover:text-red-400 cursor-pointer"><Trash2 size={15} /></button>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <Lbl label="Drop Pool ID"><input type="number" value={t.dropPoolId} onChange={(e) => set('dropPoolId', Number(e.target.value))} className={`${inp} font-mono`} /></Lbl>
          <Lbl label="Spawn Channels"><input value={t.spawnChannels} onChange={(e) => set('spawnChannels', e.target.value)} className={`${inp} font-mono`} /></Lbl>
        </div>
        <details className="group">
          <summary className="text-xs text-white/50 cursor-pointer hover:text-white/80 list-none flex items-center gap-1">▸ Avançado</summary>
          <div className="grid grid-cols-2 gap-2 mt-2">
            <Lbl label="AuthorityType"><input type="number" value={t.authorityType} onChange={(e) => set('authorityType', Number(e.target.value))} className={`${inp} font-mono`} /></Lbl>
            <Lbl label="PerfectDrop"><input type="number" value={t.perfectDrop} onChange={(e) => set('perfectDrop', Number(e.target.value))} className={`${inp} font-mono`} /></Lbl>
            <Lbl label="MultiDropType"><input type="number" value={t.multiDropType} onChange={(e) => set('multiDropType', Number(e.target.value))} className={`${inp} font-mono`} /></Lbl>
            <div />
            <Lbl label="MultiDropMin"><input type="number" value={t.multiDropMin} onChange={(e) => set('multiDropMin', Number(e.target.value))} className={`${inp} font-mono`} /></Lbl>
            <Lbl label="MultiDropMax"><input type="number" value={t.multiDropMax} onChange={(e) => set('multiDropMax', Number(e.target.value))} className={`${inp} font-mono`} /></Lbl>
          </div>
        </details>
        <button onClick={saveTarget} disabled={busy} className="w-full flex items-center justify-center gap-2 py-2 rounded-lg btn-primary font-bold text-sm cursor-pointer disabled:opacity-40">
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Salvar target
        </button>
        <DropLimitSection eventId={eventId} dungeon={target.dungeonIndex} limit={data.limits.find(l => l.dungeonIndex === target.dungeonIndex)} onChanged={onChanged} />
      </div>

      <PoolEditor eventId={eventId} poolId={target.dropPoolId} items={poolItems} refCount={poolRef} name={poolName} onChanged={onChanged} />
    </div>
  );
}

/* ------------------------------------------------ editor do drop pool */
function PoolEditor({ eventId, poolId, items, refCount, name, onChanged }: {
  eventId: number; poolId: number; items: DropPoolItem[]; refCount: number; name: string; onChanged: () => void;
}) {
  const { notify } = useToast();
  const [list, setList] = useState<DropPoolItem[]>(items);
  const [pname, setPname] = useState(name);
  const [busy, setBusy] = useState(false);
  const [pick, setPick] = useState(false);
  useEffect(() => { setList(items); setPname(name); }, [poolId]); // recarrega ao trocar de pool
  const shared = refCount > 1;
  const totalRate = list.reduce((a, b) => a + (b.dropRate || 0), 0);

  const add = (it: GameItem) => setList([...list, { order: list.length, itemKindIdx: it.itemId, itemOption: '0', itemDurationIdx: 0, dropRate: 0 }]);
  const upd = (i: number, patch: Partial<DropPoolItem>) => setList(list.map((p, j) => j === i ? { ...p, ...patch } : p));
  const rm = (i: number) => setList(list.filter((_, j) => j !== i));

  const save = async () => {
    if (!poolId) { notify('error', 'Defina o Drop Pool ID no target primeiro'); return; }
    setBusy(true);
    try {
      const r = await editDropPool(eventId, poolId, { items: list, name: pname || undefined, cloneIfShared: true });
      notify('success', r.cloned ? `Pool clonado → #${r.poolId} (original preservado)` : 'Pool salvo');
      onChanged();
    } catch (err: any) { notify('error', err?.detail || err?.message || 'Erro'); }
    finally { setBusy(false); }
  };

  return (
    <div className="surface-card rounded-xl p-5 space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-bold text-white">Drop Pool #{poolId}</h3>
        <span className="text-[11px] text-white/45">Σ rate {totalRate.toFixed(2)}</span>
      </div>
      <input value={pname} onChange={(e) => setPname(e.target.value)} placeholder="Nome do pool (opcional)" className={inp} />
      {shared && (
        <div className="flex items-start gap-2 p-2 rounded-lg bg-amber-500/10 border border-amber-500/25">
          <Link2 size={13} className="text-amber-400 shrink-0 mt-0.5" />
          <p className="text-[10px] text-amber-200/90">Pool compartilhado por {refCount} targets. Ao salvar, será CLONADO para este target (não afeta os outros).</p>
        </div>
      )}
      <div className="space-y-1.5">
        {list.map((it, i) => (
          <div key={i} className="flex items-center gap-1.5">
            <ItemKindInline value={it.itemKindIdx} onChange={(v) => upd(i, { itemKindIdx: v })} />
            <input value={it.itemOption} onChange={(e) => upd(i, { itemOption: e.target.value })} title="option"
              className="w-14 bg-[#0d141b] border border-white/[0.06] rounded px-1.5 py-1 text-[10px] text-white font-mono shrink-0" />
            <input type="number" value={it.itemDurationIdx} onChange={(e) => upd(i, { itemDurationIdx: Number(e.target.value) })} title="duration"
              className="w-12 bg-[#0d141b] border border-white/[0.06] rounded px-1.5 py-1 text-[10px] text-white font-mono shrink-0" />
            <input type="number" step="0.01" value={it.dropRate} onChange={(e) => upd(i, { dropRate: Number(e.target.value) })} title="drop rate %"
              className="w-14 bg-[#0d141b] border border-white/[0.06] rounded px-1.5 py-1 text-xs text-white font-mono shrink-0" />
            <button onClick={() => rm(i)} className="text-white/40 hover:text-red-400 cursor-pointer shrink-0"><X size={14} /></button>
          </div>
        ))}
        <button onClick={() => setPick(true)} className="w-full flex items-center justify-center gap-1.5 py-1.5 rounded-lg border border-dashed border-white/[0.1] text-[11px] text-white/50 hover:text-white hover:border-white/25 cursor-pointer">
          <Plus size={13} /> Adicionar item
        </button>
      </div>
      <button onClick={save} disabled={busy} className="w-full flex items-center justify-center gap-2 py-2 rounded-lg btn-primary font-bold text-sm cursor-pointer disabled:opacity-40">
        {busy ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Salvar pool
      </button>
      <GameItemPicker open={pick} onClose={() => setPick(false)} title="Item do drop pool" onPick={add} />
    </div>
  );
}

/* ------------------------------------------------ limites de dungeon (opcional) */
function DropLimitSection({ eventId, dungeon, limit, onChanged }: {
  eventId: number; dungeon: number; limit?: DropLimit; onChanged: () => void;
}) {
  const { notify } = useToast();
  const [open, setOpen] = useState(false);
  const [l, setL] = useState<DropLimit>(limit ?? {
    dungeonIndex: dungeon, entryCountLimit: 0, entryItemKind: '0', entryItemOption: '0',
    entryForcegem: 0, limitItemKind: '0', limitItemOption: '0', dropCountLimit: 0,
  });
  const set = (k: keyof DropLimit, v: any) => setL(p => ({ ...p, [k]: v }));
  const save = async () => {
    try { await saveDropLimit(eventId, dungeon, { ...l, dungeonIndex: dungeon }); notify('success', 'Limite salvo'); onChanged(); }
    catch (err: any) { notify('error', err?.detail || err?.message || 'Erro'); }
  };
  const del = async () => {
    try { await deleteDropLimit(eventId, dungeon); notify('success', 'Limite removido'); onChanged(); }
    catch (err: any) { notify('error', err?.detail || err?.message || 'Erro'); }
  };
  return (
    <div className="pt-2 border-t border-white/[0.05]">
      <button onClick={() => setOpen(o => !o)} className="text-xs text-white/50 hover:text-white/80 cursor-pointer">{open ? '▾' : '▸'} Limites de entrada/drop</button>
      {open && (
        <div className="grid grid-cols-2 gap-2 mt-2">
          <Lbl label="Entry Count"><input type="number" value={l.entryCountLimit} onChange={(e) => set('entryCountLimit', Number(e.target.value))} className={`${inp} font-mono`} /></Lbl>
          <Lbl label="Entry Forcegem"><input type="number" value={l.entryForcegem} onChange={(e) => set('entryForcegem', Number(e.target.value))} className={`${inp} font-mono`} /></Lbl>
          <Lbl label="Entry Item Kind"><input value={l.entryItemKind} onChange={(e) => set('entryItemKind', e.target.value)} className={`${inp} font-mono`} /></Lbl>
          <Lbl label="Entry Item Option"><input value={l.entryItemOption} onChange={(e) => set('entryItemOption', e.target.value)} className={`${inp} font-mono`} /></Lbl>
          <Lbl label="Limit Item Kind"><input value={l.limitItemKind} onChange={(e) => set('limitItemKind', e.target.value)} className={`${inp} font-mono`} /></Lbl>
          <Lbl label="Drop Count Limit"><input type="number" value={l.dropCountLimit} onChange={(e) => set('dropCountLimit', Number(e.target.value))} className={`${inp} font-mono`} /></Lbl>
          <div className="col-span-2 flex gap-2">
            <button onClick={save} className="flex-1 py-1.5 rounded-lg bg-white/[0.06] text-white/80 hover:bg-white/[0.1] text-xs cursor-pointer">Salvar limite</button>
            {limit && <button onClick={del} className="px-3 py-1.5 rounded-lg bg-red-500/10 text-red-300 hover:bg-red-500/20 text-xs cursor-pointer">Remover</button>}
          </div>
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------ add target modal */
function AddTargetModal({ eventId, onClose, onAdded }: { eventId: number; onClose: () => void; onAdded: () => void }) {
  const { notify } = useToast();
  const [t, setT] = useState<DropTarget>(emptyTarget());
  const [busy, setBusy] = useState(false);
  const set = (k: keyof DropTarget, v: any) => setT(p => ({ ...p, [k]: v }));
  const add = async () => {
    setBusy(true);
    try { await addDropTarget(eventId, t); notify('success', 'Target adicionado'); onAdded(); }
    catch (err: any) { notify('error', err?.detail || err?.message || 'Erro'); setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="surface-card rounded-2xl p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4"><h2 className="text-lg font-bold text-white">Adicionar Target</h2><button onClick={onClose} className="text-white/55 hover:text-white cursor-pointer"><X size={18} /></button></div>
        <div className="grid grid-cols-2 gap-3">
          <Lbl label="Dungeon Index"><input type="number" value={t.dungeonIndex} onChange={(e) => set('dungeonIndex', Number(e.target.value))} className={`${inp} font-mono`} /></Lbl>
          <Lbl label="Mob Species Idx"><input type="number" value={t.mobSpeciesIdx} onChange={(e) => set('mobSpeciesIdx', Number(e.target.value))} className={`${inp} font-mono`} /></Lbl>
          <Lbl label="Drop Pool ID"><input type="number" value={t.dropPoolId} onChange={(e) => set('dropPoolId', Number(e.target.value))} className={`${inp} font-mono`} /></Lbl>
          <Lbl label="Spawn Channels"><input value={t.spawnChannels} onChange={(e) => set('spawnChannels', e.target.value)} className={`${inp} font-mono`} /></Lbl>
        </div>
        <p className="text-[10px] text-white/40 mt-2">Deixe o Pool ID como um número novo para criar um pool vazio (edite os itens depois).</p>
        <div className="flex gap-2 mt-5">
          <button onClick={onClose} className="flex-1 py-2.5 rounded-lg bg-white/[0.04] text-white/75 hover:bg-white/[0.08] text-sm cursor-pointer">Cancelar</button>
          <button onClick={add} disabled={busy} className="flex-1 py-2.5 rounded-lg btn-primary font-bold text-sm cursor-pointer disabled:opacity-40 flex items-center justify-center gap-2">
            {busy && <Loader2 size={14} className="animate-spin" />} Adicionar
          </button>
        </div>
      </div>
    </div>
  );
}

const inp = 'w-full bg-[#0d141b] border border-white/[0.06] rounded-lg px-2.5 py-1.5 text-sm text-white';
function Lbl({ label, children }: { label: string; children: React.ReactNode }) {
  return <div><label className="text-[10px] text-white/50 mb-1 block">{label}</label>{children}</div>;
}
